

import { defineComponent, computed, PropType, watch } from 'vue'
import { myValidationSchemaObj, validationMessage } from '@/views/validation'
import { useForm } from 'vee-validate'
import { object, string } from 'yup'
import { FacilityInfoUiItem } from '@/repositories/commons/corporationInfoRepository'
import {
  prefectures,
  hospitalClinicKbns,
  hasCodes,
  taxAccountingMethods,
  hasBedFunctionReports,
  specialties,
  outputSettings
} from '@/views/corporation-info/selectOptions'
import { HasCodeType, hospitalClinicKbnType, OutputSettingType } from '@/views/corporation-info/types'

type InputForm = {
  hospitalClinicKbn: string;
  prefectureCode: string
  hasBedOutpatientMgmtNumber: string
  bedOutpatientMgmtNumber: string
  hasMedicalInstitutionCode: string
  medicalInstitutionCode: string
  hospitalClinicName: string
  executiveCount: number | null
  staffCount: number | null
  prefecture: string
  municipality: string
  townArea: string
  secondaryMedicalArea: string
  taxAccountingMethod: string
  primarySpecialty1: string
  primarySpecialty2: string
  primarySpecialty3: string
  hasBedFunctionReport: string
  outputSetting: string
  bmnGroup: string
}

const validationSchema = object({
  // primarySpecialty2、primarySpecialty3、outputSettingはバリデーション不要
  hospitalClinicKbn: myValidationSchemaObj.requiredString,
  prefectureCode: myValidationSchemaObj.requiredString,
  hasBedOutpatientMgmtNumber: myValidationSchemaObj.requiredString,
  bedOutpatientMgmtNumber: myValidationSchemaObj.fixedLengthNumberIfFieldEquals('hasBedOutpatientMgmtNumber', HasCodeType.Yes, 8),
  hasMedicalInstitutionCode: myValidationSchemaObj.requiredString,
  medicalInstitutionCode: myValidationSchemaObj.fixedLengthNumberIfFieldEquals('hasMedicalInstitutionCode', HasCodeType.Yes, 10),
  hospitalClinicName: myValidationSchemaObj.requiredString,
  executiveCount: myValidationSchemaObj.requiredNumberIfFieldNotEquals('hospitalClinicKbn', hospitalClinicKbnType.NonReportable),
  staffCount: myValidationSchemaObj.requiredNumberIfFieldNotEquals('hospitalClinicKbn', hospitalClinicKbnType.NonReportable),
  prefecture: myValidationSchemaObj.requiredString,
  municipality: myValidationSchemaObj.requiredString,
  townArea: myValidationSchemaObj.requiredString,
  secondaryMedicalArea: myValidationSchemaObj.requiredStringIfFieldNotEquals('hospitalClinicKbn', hospitalClinicKbnType.NonReportable),
  taxAccountingMethod: myValidationSchemaObj.requiredStringIfFieldNotEquals('hospitalClinicKbn', hospitalClinicKbnType.NonReportable),
  primarySpecialty1: myValidationSchemaObj.requiredIfFieldEquals('hospitalClinicKbn', hospitalClinicKbnType.Clinic),
  primarySpecialty2: string().trim(),
  primarySpecialty3: string().trim(),
  hasBedFunctionReport: myValidationSchemaObj.requiredStringIfFieldNotEquals('hospitalClinicKbn', hospitalClinicKbnType.NonReportable),
  bmnGroup: myValidationSchemaObj.requiredIfTwoFields('outputSetting', OutputSettingType.BmnGroup, 'hospitalClinicKbn', hospitalClinicKbnType.NonReportable)
  // bmnGroup: myValidationSchemaObj.requiredIfFieldEquals('outputSetting', OutputSettingType.BmnGroup)
})

export default defineComponent({
  props: {
    item: {
      type: Object as PropType<FacilityInfoUiItem>,
      required: true
    },
    isFirst: {
      type: Boolean,
      required: false,
      default: false
    },
    isLast: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: {
    upItem: (item: FacilityInfoUiItem) => true,
    downItem: (item: FacilityInfoUiItem) => true,
    trashItem: (item: FacilityInfoUiItem) => true,
    changeInput: (item: FacilityInfoUiItem) => true
  },
  setup (props, { emit }) {
    const { errors, values, meta, validate } = useForm<InputForm>({
      validationSchema: validationSchema,
      initialValues: {
        hospitalClinicKbn: props.item.item.hospitalClinicKbn,
        prefectureCode: props.item.item.prefectureCode,
        hasBedOutpatientMgmtNumber: props.item.item.hasBedOutpatientMgmtNumber,
        bedOutpatientMgmtNumber: props.item.item.bedOutpatientMgmtNumber,
        hasMedicalInstitutionCode: props.item.item.hasMedicalInstitutionCode,
        medicalInstitutionCode: props.item.item.medicalInstitutionCode,
        hospitalClinicName: props.item.item.hospitalClinicName,
        executiveCount: props.item.item.executiveCount,
        staffCount: props.item.item.staffCount,
        prefecture: props.item.item.prefecture,
        municipality: props.item.item.municipality,
        townArea: props.item.item.townArea,
        secondaryMedicalArea: props.item.item.secondaryMedicalArea,
        taxAccountingMethod: props.item.item.taxAccountingMethod,
        primarySpecialty1: props.item.item.primarySpecialty1,
        primarySpecialty2: props.item.item.primarySpecialty2,
        primarySpecialty3: props.item.item.primarySpecialty3,
        hasBedFunctionReport: props.item.item.hasBedFunctionReport,
        outputSetting: props.item.item.outputSetting,
        bmnGroup: props.item.item.bmnGroup
      }
    })
    const headerName = computed(() => {
      if (!values.hospitalClinicName) {
        return validationMessage.enterItem
      }
      return values.hospitalClinicName
    })

    // 病床・外来管理番号有無が無の場合、病床・外来管理番号をクリア
    watch(() => values.hasBedOutpatientMgmtNumber, (newVal) => {
      if (newVal === HasCodeType.No) {
        values.bedOutpatientMgmtNumber = ''
      }
    })

    // 医療機関コード有無が無の場合、医療機関コードをクリア
    watch(() => values.hasMedicalInstitutionCode, (newVal) => {
      if (newVal === HasCodeType.No) {
        values.medicalInstitutionCode = ''
      }
    })

    // 出力設定が全社の場合、部門グループをクリア
    watch(() => values.outputSetting, (newVal) => {
      if (newVal === OutputSettingType.All || newVal === '') {
        values.bmnGroup = ''
      }
    })

    // 病院・診療所区分の監視
    watch(() => values.hospitalClinicKbn, (newVal) => {
      // 様式１か様式３の場合
      if (newVal === hospitalClinicKbnType.Hospital || newVal === hospitalClinicKbnType.NonReportable) {
        values.primarySpecialty1 = ''
        values.primarySpecialty2 = ''
        values.primarySpecialty3 = ''
        // 様式３の場合
        if (newVal === hospitalClinicKbnType.NonReportable) {
          values.executiveCount = null
          values.staffCount = null
          values.secondaryMedicalArea = ''
          values.taxAccountingMethod = ''
          values.hasBedFunctionReport = ''
        }
      }
    })

    const onClickedUpItem = () => {
      emit('upItem', props.item)
    }

    const onClickedDownItem = () => {
      emit('downItem', props.item)
    }

    const onClickedTrashItem = () => {
      emit('trashItem', props.item)
    }

    const onChange = async () => {
      const item = props.item
      item.item.hospitalClinicKbn = values.hospitalClinicKbn
      item.item.prefectureCode = values.prefectureCode
      item.item.hasBedOutpatientMgmtNumber = values.hasBedOutpatientMgmtNumber
      item.item.bedOutpatientMgmtNumber = values.bedOutpatientMgmtNumber
      item.item.hasMedicalInstitutionCode = values.hasMedicalInstitutionCode
      item.item.medicalInstitutionCode = values.medicalInstitutionCode
      item.item.hospitalClinicName = values.hospitalClinicName
      item.item.executiveCount = values.executiveCount
      item.item.staffCount = values.staffCount
      item.item.prefecture = values.prefecture
      item.item.municipality = values.municipality
      item.item.townArea = values.townArea
      item.item.secondaryMedicalArea = values.secondaryMedicalArea
      item.item.taxAccountingMethod = values.taxAccountingMethod
      item.item.primarySpecialty1 = values.primarySpecialty1
      item.item.primarySpecialty2 = values.primarySpecialty2
      item.item.primarySpecialty3 = values.primarySpecialty3
      item.item.hasBedFunctionReport = values.hasBedFunctionReport
      item.item.outputSetting = values.outputSetting
      item.item.bmnGroup = values.bmnGroup
      await validate()
      item.isValid = meta.value.valid
      emit('changeInput', item)
    }

    return {
      HasCodeType,
      hospitalClinicKbnType,
      OutputSettingType,
      headerName,
      errors,
      values,
      validate,
      prefectures,
      hospitalClinicKbns,
      hasCodes,
      taxAccountingMethods,
      hasBedFunctionReports,
      specialties,
      outputSettings,
      onClickedUpItem,
      onClickedDownItem,
      onClickedTrashItem,
      onChange
    }
  }
})

